import type { RendererFieldInput } from "./types";

export interface RendererField extends RendererFieldInput {
  rules: ((value: unknown) => boolean | string) | undefined;
}

export interface ParsedSchema {
  fields: RendererField[];
  initialValues: Record<string, unknown>;
}

export type ComponentResolver = (fieldType: string) => unknown;

interface HubspotField {
  name: string;
  label: string;
  fieldType: string;
  placeholder?: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>;
}

interface HubspotFormSchema {
  formFieldGroups?: Array<{
    fields: HubspotField[];
  }>;
  fieldGroups?: Array<{
    fields: HubspotField[];
  }>;
}

const requiredRule = (label: string) => (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return `${label} is required`;
  }
  return true;
};

export const fromSchema = (schema: unknown, resolveComponent: ComponentResolver): ParsedSchema => {
  const hubspotSchema = schema as HubspotFormSchema;
  // Support both v2 (formFieldGroups) and v3 (fieldGroups) API responses
  const groups = hubspotSchema.fieldGroups ?? hubspotSchema.formFieldGroups ?? [];
  const hubspotFields = groups.flatMap((group) => group.fields);

  const fields: RendererField[] = [];
  const initialValues: Record<string, unknown> = {};

  for (const field of hubspotFields) {
    // Skip file fields - handled separately by FileUpload component
    if (field.fieldType === 'file') {
      continue;
    }

    const component = resolveComponent(field.fieldType);
    const options = field.options?.map((option) => ({
      label: option.label,
      value: option.value
    }));

    const rendererField: RendererField = {
      name: field.name,
      label: field.label,
      component,
      placeholder: field.placeholder ?? "",
      inputType: mapInputType(field.fieldType),
      options,
      required: field.required,
      rules: undefined
    };

    if (field.required === true) {
      rendererField.rules = requiredRule(field.label);
    }

    fields.push(rendererField);
    initialValues[field.name] = "";
  }

  return {
    fields,
    initialValues
  };
};

const mapInputType = (fieldType: string) => {
  const normalized = fieldType.toLowerCase();
  if (normalized === "email") {
    return "email";
  }
  if (normalized === "phone") {
    return "tel";
  }
  return "text";
};
